/**
 * The ellipsis menu on a listing you own.
 *
 * WHY THIS EXISTS. Sellers were filing abuse reports against their own listings.
 * Report was the only thing behind the ellipsis on a product page, so a seller
 * who wanted to take an item down or close it out reached for the only control
 * on offer — and the reports landed in the moderation queue as if the listing
 * were spam.
 *
 * So on your own listing the ellipsis opens this instead of ReportSubMenu.
 * Everyone else's ellipsis is unchanged: a seller cannot report their own
 * listing any more, and a shopper's report path is untouched.
 *
 * Two of the four items act in place — Mark Sold and Delist/Relist write through
 * the context and patch the product's status, so the page redraws around the new
 * state without a navigation. The other two are links: Edit and Manage Orders
 * both need the account area's forms, which this page does not carry.
 */
import { Fragment, RefObject, useState } from "react";
import shared from "../data/js/shared";
import {
  useUI,
  useNotifications,
  useProduct,
  useListing,
  useRouterContext,
} from "../contexts";
import SubMenuWrapper from "./sub-menu-wrapper";
import {
  ARCHIVED,
  LISTED,
  CHATTING,
  PURCHASED,
  HELD,
  SOLD,
} from "../data/js/listing-statuses";

const {
  markSoldLabel,
  editLabel,
  delistLabel,
  relistLabel,
  manageOrdersLabel,
  workingLabel,
  markSoldSuccess,
  markSoldError,
  delistSuccess,
  delistError,
  relistSuccess,
  relistError,
  staleStatusError,
  notYoursError,
} = shared.ownerMenu;

/** Marking sold only makes sense while the listing is live or mid-sale. */
const CAN_MARK_SOLD = new Set<number>([LISTED, CHATTING, PURCHASED, HELD]);
/** Mirrors DELISTABLE in /api/seller/listings/bulk-status. */
const CAN_DELIST = new Set<number>([LISTED, CHATTING]);
/** Mirrors RELISTABLE in /api/seller/listings/bulk-status. */
const CAN_RELIST = new Set<number>([ARCHIVED, SOLD]);
/** From PURCHASED on there is an order behind the listing to manage. */
const HAS_ORDERS = new Set<number>([PURCHASED, HELD, SOLD]);

interface OwnerSubMenuProps {
  triggerRef?: RefObject<HTMLElement | null>;
}

const OwnerSubMenu = ({ triggerRef }: OwnerSubMenuProps) => {
  const { productDetail, applyProductStatus } = useProduct();
  const { markListingSold, setListingSaleStatus } = useListing();
  const { setReportMenuOpen, reportMenuOpen } = useUI();
  const { createNotification } = useNotifications();
  const { push } = useRouterContext();
  /**
   * Which item is mid-flight, not a bare boolean: the label swap needs to land
   * on the item the seller clicked. A shared "working" state relabelled every
   * item at once, which read as though the menu had lost its contents.
   */
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const busy = busyKey !== null;

  const id = productDetail?.id;
  // The listing API returns a number and the edit form round-trips a string;
  // both reach this component, so normalise once rather than at each gate.
  const status = Number(productDetail?.status);
  if (!id || Number.isNaN(status)) return null;

  const notify = (type: "success" | "error", msg: string) =>
    createNotification({ type, msg, dismissed: false });

  const closeMenu = () => setReportMenuOpen(false);

  /**
   * `nextStatus` is applied only on success, and only after the server has
   * confirmed the write — showing "Sold" over a request that failed is worse
   * than showing the old state, because the seller walks away believing it.
   */
  const run = async (
    key: string,
    action: () => Promise<{ ok: boolean; reason?: string }>,
    nextStatus: number,
    successMsg: string,
    failureMsg: string,
  ) => {
    if (busy) return;
    setBusyKey(key);
    closeMenu();
    try {
      const result = await action();
      if (result.ok) {
        applyProductStatus(id, nextStatus);
        notify("success", successMsg);
        return;
      }
      if (result.reason === "sale-in-progress") {
        notify("error", staleStatusError);
        return;
      }
      if (result.reason === "not-yours") {
        notify("error", notYoursError);
        return;
      }
      notify("error", failureMsg);
    } catch {
      // Both context methods swallow their own transport errors, so this is the
      // belt-and-braces path. Without it a rejection would escape a `void`-ed
      // handler as an unhandled rejection and the seller would be left with a
      // menu that closed and did nothing.
      notify("error", failureMsg);
    } finally {
      setBusyKey(null);
    }
  };

  const markSold = () =>
    run(
      "mark-sold",
      async () => {
        const response = await markListingSold(id);
        return { ok: Boolean(response.success), reason: "failed" };
      },
      SOLD,
      markSoldSuccess,
      markSoldError,
    );

  const delist = () =>
    run("delist", () => setListingSaleStatus(id, "delist"), ARCHIVED, delistSuccess, delistError);

  const relist = () =>
    run("relist", () => setListingSaleStatus(id, "relist"), LISTED, relistSuccess, relistError);

  const goTo = (path: string) => {
    closeMenu();
    push(path);
  };

  const items: { key: string; label: string; testId: string; onClick: () => void }[] = [];

  if (CAN_MARK_SOLD.has(status)) {
    items.push({
      key: "mark-sold",
      label: busyKey === "mark-sold" ? workingLabel : markSoldLabel,
      testId: "owner-menu-mark-sold",
      onClick: () => void markSold(),
    });
  }
  if (status < SOLD) {
    items.push({
      key: "edit",
      label: editLabel,
      testId: "owner-menu-edit",
      onClick: () => goTo(`/account/my-listings/${id}/`),
    });
  }
  if (CAN_DELIST.has(status)) {
    items.push({
      key: "delist",
      label: busyKey === "delist" ? workingLabel : delistLabel,
      testId: "owner-menu-delist",
      onClick: () => void delist(),
    });
  }
  if (CAN_RELIST.has(status)) {
    items.push({
      key: "relist",
      label: busyKey === "relist" ? workingLabel : relistLabel,
      testId: "owner-menu-relist",
      onClick: () => void relist(),
    });
  }
  if (HAS_ORDERS.has(status)) {
    items.push({
      key: "manage-orders",
      label: manageOrdersLabel,
      testId: "owner-menu-manage-orders",
      onClick: () => goTo(`/account/sales/${id}/`),
    });
  }

  return (
    <SubMenuWrapper
      className="sub-menu-cont owner-menu-cont"
      onChange={closeMenu}
      handle="owner-submenu-handle"
      expanded={reportMenuOpen}
      triggerRef={triggerRef}
      dynamicPosition={true}
    >
      <div className="sub-menu owner-menu" data-testid="owner-menu">
        {/* Fragments, not wrappers: `.sub-menu` lays its children out as a
            flex column and `.sub-menu-item:first-child`/`:last-child` round the
            menu's corners, both of which need the items and separators to stay
            DIRECT children. */}
        {items.map((item, index) => (
          <Fragment key={item.key}>
            {index > 0 && <div className="line" />}
            <div
              className={`sub-menu-item${busyKey === item.key ? " is-busy" : ""}`}
              data-testid={item.testId}
              onClick={item.onClick}
            >
              {item.label}
            </div>
          </Fragment>
        ))}
        <img src="/images/submenu-pointer.png" className="pointer" alt="" />
      </div>
    </SubMenuWrapper>
  );
};

export { OwnerSubMenu };
export default OwnerSubMenu;
